import { useCallback, useContext, useSyncExternalStore } from 'react';
import { RoomEvent } from 'livekit-client';
import { VideoContext } from '../contexts/VideoContext.js';

/**
 * Whether the browser is blocking audio playback for the current call, which
 * is exactly when `VideoRoomAudio` shows its "Click to enable audio" control.
 *
 * Both read the same room: `room.canPlaybackAudio`, updated with
 * `RoomEvent.AudioPlaybackStatusChanged` (LiveKit's `useAudioPlayback` does
 * the same). This one also works outside the room's `RoomContext`, so the
 * toasts can stay clear of the control (#131). No room means no call, and no
 * control.
 */
export function useAudioPlaybackBlocked(): boolean {
  const room = useContext(VideoContext)?.room ?? null;

  const subscribe = useCallback((onChange: () => void) => {
    if (!room) return () => undefined;
    room.on(RoomEvent.AudioPlaybackStatusChanged, onChange);
    return () => {
      room.off(RoomEvent.AudioPlaybackStatusChanged, onChange);
    };
  }, [room]);

  const getSnapshot = useCallback(() => (room ? !room.canPlaybackAudio : false), [room]);

  return useSyncExternalStore(subscribe, getSnapshot);
}
